import FuseScrollbars from '@fuse/core/FuseScrollbars';
import { styled, darken } from '@mui/material/styles';
import clsx from 'clsx';
import { memo, useEffect, useState } from 'react';
import Navigation from 'src/components/theme-layouts/components/navigation/Navigation';
import Logo from '../../../../components/Logo';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

const Root = styled('div')(({ theme }) => ({
	backgroundColor: theme.vars.palette.background.default,
	color: theme.vars.palette.text.primary,
	'& ::-webkit-scrollbar-thumb': {
		boxShadow: `inset 0 0 0 20px ${'rgba(255, 255, 255, 0.24)'}`,
		...theme.applyStyles('light', {
			boxShadow: `inset 0 0 0 20px ${'rgba(0, 0, 0, 0.24)'}`
		})
	},
	'& ::-webkit-scrollbar-thumb:active': {
		boxShadow: `inset 0 0 0 20px ${'rgba(255, 255, 255, 0.37)'}`,
		...theme.applyStyles('light', {
			boxShadow: `inset 0 0 0 20px ${'rgba(0, 0, 0, 0.37)'}`
		})
	}
}));

const StyledContent = styled(FuseScrollbars)(() => ({
	overscrollBehavior: 'contain',
	overflowX: 'hidden',
	overflowY: 'auto',
	WebkitOverflowScrolling: 'touch',
	backgroundRepeat: 'no-repeat',
	backgroundSize: '100% 40px, 100% 10px',
	backgroundAttachment: 'local, scroll'
}));

type NavbarStyle1ContentProps = {
	className?: string;
};

function decodeJwtEmail(): string | null {
	if (typeof window === 'undefined') return null;
	try {
		const token = localStorage.getItem('orchestrator_token');
		if (!token) return null;
		const payload = token.split('.')[1];
		const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
		return decoded.email || decoded.sub || decoded.unique_name ||
			decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || null;
	} catch {
		return null;
	}
}

/**
 * The navbar style 1 content.
 */
function NavbarStyle1Content(props: NavbarStyle1ContentProps) {
	const { className = '' } = props;
	const [email, setEmail] = useState<string | null>(null);

	useEffect(() => {
		setEmail(decodeJwtEmail());
	}, []);

	const initial = email ? email[0].toUpperCase() : null;

	return (
		<Root className={clsx('flex h-full flex-auto flex-col overflow-hidden', className)}>
			<div className="flex h-12 shrink-0 flex-row items-center px-5 md:h-16">
				<Logo />
			</div>

			{email && (
				<>
					<div className="mx-3 mb-3 flex items-center gap-3 rounded-lg border border-divider px-3 py-2.5">
						<Avatar
							sx={(theme) => ({
								background: darken(theme.palette.background.default, 0.1),
								color: theme.vars.palette.text.secondary,
								width: 34,
								height: 34,
								fontSize: '0.9rem',
								fontWeight: 600,
								flexShrink: 0
							})}
							variant="rounded"
						>
							{initial}
						</Avatar>
						<div className="flex min-w-0 flex-col">
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ lineHeight: 1.3 }}
							>
								Signed in as
							</Typography>
							<Typography
								variant="body2"
								className="truncate font-medium"
								title={email}
								sx={{ lineHeight: 1.4 }}
							>
								{email}
							</Typography>
						</div>
					</div>
					<Divider />
				</>
			)}

			<StyledContent
				className="flex min-h-0 flex-1 flex-col mt-3"
				option={{ suppressScrollX: true, wheelPropagation: false }}
			>
				<Navigation layout="vertical" />
			</StyledContent>
		</Root>
	);
}

export default memo(NavbarStyle1Content);
